import { addClass, removeClass } from './class_helper';
import { commonValidations, fieldsValidations } from '../datas/_validation_rules';


/**
 * Check if a collection of fields pass their validations
 *
 * @param   {array}  fieldIds - Collection of fields id to validate
 * @param   {object} _globalErrorsAnchor - Anchor element to attach global error.
 * @returns {boolean}         - False if any error is raised, true instead
 */
export function validateFields(fields, _globalErrorsAnchor=null) {
  let errors = 0;
  fields.forEach( field => {
    if (!validateField(field, _globalErrorsAnchor)) errors += 1;
  });
  return (errors == 0)
}

/**
 * Check if a field pass its validations
 *
 * @param   {object} field - Element from document.
 * @param   {object} _globalErrorsAnchor - Anchor element to attach global error.
 * @returns {boolean}      - False if any error is raised, true instead
 */
export function validateField(field, _globalErrorsAnchor=null) {
  if (field.type == "checkbox") return true;
  
  const value = field.value;

  const {valid, errorMsg} = _applyFieldValidations(field, value);
  if (valid) {
    _removeError(field, _globalErrorsAnchor);
    return true;
  }
  else {
    _addError(field, errorMsg, _globalErrorsAnchor);
    return false;
  }
}

/**
 * Apply the validation suite
 *
 * @param  {object}          field   - Field javascript objet
 * @return {boolean, string} valid   - false if one validation fail, true instead
 *                           erroMsg - The error message of the first error raised, null instead
 */
function _applyFieldValidations(field) {
  const value = field.value;

  const fieldValidations = fieldsValidations[field.id.replace(/-\d{1}$/g, '').replace(/-/g, '_')];
  if (fieldValidations && (fieldValidations.required || (!fieldValidations.required && !(value === "" || value === undefined || value === null)))) {
    if (fieldValidations.beforeValidations) {
      let { valid, errorMsg } = _applyCommonValidations(value, fieldValidations.beforeValidations);
      if (!valid) return { valid, errorMsg };
    }
    if (fieldValidations.specificValidation && !fieldValidations.specificValidation.validate(value))
      return { valid: false, errorMsg: fieldValidations.specificValidation.errorMsg };
    if (fieldValidations.afterValidations) {
      let { valid, errorMsg } = _applyCommonValidations(value, fieldValidations.afterValidations);
      if (!valid) return { valid, errorMsg };
    }
  }
  return { valid: true };
}

/**
 * Test the common validations on the given value
 *
 * @param  {integer}         value         - Field value
 * @param  {integer}         validationIds - List of validations id to test on value
 * @return {boolean, string} valid         - false if validation fail, true instead
 *                           erroMsg       - The error message of the first error raised, null instead
 */
function _applyCommonValidations(value, validationIds) {
  let errorMsg = null;
  let valid = validationIds.every( validationId => {
    let commonValidation = commonValidations[validationId];
    if (commonValidation) {
      if (!commonValidation.validate(value)) {
        errorMsg = commonValidation.errorMsg;
        return false;
      }
    }
    return true;
  });
  return { valid: valid, errorMsg: errorMsg };
}

/**
 * Do all modifications to display the error message of the given field
 *
 * @param {object} field    - Element from document that we have to show its error message.
 * @param {string} errorMsg - Message to display.
 * @param {object} _globalErrorsAnchor - Anchor element to attach global error.
 */
function _addError(field, errorMsg, _globalErrorsAnchor=null) {
  addClass(field, 'is-invalid-input');
  if (_globalErrorsAnchor) { // Attach message to the gobal errors messages explanation
    _addErrorToGlobal(field, errorMsg, _globalErrorsAnchor);
  } else { // Attach errorś message to the corresponding field
    addClass(field.closest('label'), 'is-invalid-label');
    const errorAnchor         = document.getElementById(field.id+'-error');
    errorAnchor.innerHTML     = errorMsg;
    errorAnchor.style.display = 'block';
  }
}

/**
 * Do all modifications to attach the error message of the given field to the
 *  global errors anchor element
 *
 * @param {object} field    - Element from document that we have to show its error message.
 * @param {string} errorMsg - Message to display.
 * @param {object} _globalErrorsAnchor - Anchor element to attach global error.
 */
function _addErrorToGlobal(field, errorMsg, _globalErrorsAnchor) {
  const fieldErrorMsgId = 'error-msg-' + field.id;
  let fieldErrorMsg     = _globalErrorsAnchor.querySelector('#' + fieldErrorMsgId);
  if (!fieldErrorMsg) {
    fieldErrorMsg           = document.createElement('div');
    fieldErrorMsg.id        = fieldErrorMsgId;
    fieldErrorMsg.className = 'field-error';
    const groupNumber       = field.id.slice(-1);
    const groupsErrorsMsgId = 'group-errors-msg-' + groupNumber;
    let groupErrorsMsg = _globalErrorsAnchor.querySelector('#' + groupsErrorsMsgId);
    if (!groupErrorsMsg) {
      groupErrorsMsg           = document.createElement('div');
      groupErrorsMsg.id        = groupsErrorsMsgId;
      groupErrorsMsg.className = 'group-errors';
      let groupErrorsTitle = document.createElement('div');
      groupErrorsTitle.className = 'group-title';
      groupErrorsTitle.innerHTML = field.getAttribute('data-group') + ':';
      groupErrorsMsg.append(groupErrorsTitle);
      _globalErrorsAnchor.append(groupErrorsMsg);
    }
    groupErrorsMsg.append(fieldErrorMsg) ;
  }
  let fieldName           = document.createElement('span');
  fieldName.className     = 'field-name';
  fieldName.innerHTML     = field.getAttribute('data-name') + ' : ';
  fieldErrorMsg.innerHTML = '';
  fieldErrorMsg.append(fieldName);
  fieldErrorMsg.append(errorMsg);
}

/**
 * Do all modifications to remove the error message of the given field
 *
 * @param {object} field - Element from document that we have to remove its error message.
 * @param {object} _globalErrorsAnchor - Anchor element to attach global error.
 */
function _removeError(field, _globalErrorsAnchor=null) {
  const errorAnchor = document.getElementById(field.id+'-error');
  removeClass(field, 'is-invalid-input');
  if (_globalErrorsAnchor) { // Remove error message from the gobal errors messages explanation
    _removeErrorFromGlobal(field, _globalErrorsAnchor)
  } else { // Remove error message attached to the corresponding field
    removeClass(field.closest('label'), 'is-invalid-label');
    errorAnchor.innerHTML     = '';
    errorAnchor.style.display = 'none';
  }
}

/**
 * Do all modifications to remove the error message of the given field from the
 *  global errors anchor element
 *
 * @param {object} field - Element from document that we have to remove its error message.
 * @param {object} _globalErrorsAnchor - Anchor element to attach global error.
 */
function _removeErrorFromGlobal(field, _globalErrorsAnchor) {
  const groupNumber   = field.id.slice(-1);
  let groupErrorsMsg  = _globalErrorsAnchor.querySelector('#group-errors-msg-' + groupNumber);
  const fieldErrorMsg = _globalErrorsAnchor.querySelector('#error-msg-' + field.id);
  if (fieldErrorMsg)
    fieldErrorMsg.outerHTML = '';
  if (groupErrorsMsg && groupErrorsMsg.childNodes.length == 1)
    groupErrorsMsg.outerHTML = '';
}



// WEBPACK FOOTER //
// ./assets/javascripts/helpers/validations_helper.js